<template>
  <AcademyLayout @enroll="enrollOpen = true">
    <div v-if="!course" class="ac-wrap ac-empty">
      <p>未找到该课程</p>
      <router-link :to="{ name: 'training' }">返回课程列表</router-link>
    </div>

    <template v-else>
      <!-- 顶部课程头图区（慕课/云课堂风格） -->
      <section class="cd-hero" :class="`cd-hero--${course.coverTone}`">
        <div class="ac-wrap cd-hero__inner">
          <nav class="cd-crumb">
            <router-link :to="{ name: 'training' }">高仁学堂</router-link>
            <span>/</span>
            <router-link :to="{ name: 'training' }">{{ course.category }}</router-link>
            <span>/</span>
            <em>{{ course.shortTitle || course.title }}</em>
          </nav>

          <div class="cd-hero__grid">
            <div class="cd-hero__main">
              <span class="cd-hero__badge">{{ course.shortTitle || course.category }}</span>
              <h1>{{ course.title }}</h1>
              <p class="cd-hero__desc">{{ course.summary }}</p>
              <div class="cd-hero__stats">
                <span><strong>{{ course.learners }}</strong> 人已报名</span>
                <span><strong>{{ course.views || '—' }}</strong> 次浏览</span>
                <span><strong>{{ course.chapters.length }}</strong> 个章节</span>
                <span><strong>{{ course.duration }}</strong> 课时</span>
              </div>
              <div class="cd-hero__tags">
                <span v-for="tag in course.tags" :key="tag">{{ tag }}</span>
              </div>
            </div>

            <div class="cd-hero__media" aria-hidden="true">
              <div class="cd-hero__poster">
                <strong>{{ course.shortTitle || course.category }}</strong>
                <em>{{ course.category }} · {{ course.duration }}</em>
                <button type="button" class="cd-hero__play" @click="tab = 'chapters'; scrollToPanel()">
                  <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
                    <path fill="currentColor" d="M8 5v14l11-7z" />
                  </svg>
                  查看课程目录
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="ac-wrap cd-body">
        <div class="cd-layout">
          <!-- 左侧主内容 -->
          <div class="cd-main">
            <div class="cd-tabs" ref="panelRef">
              <button
                v-for="item in tabs"
                :key="item.key"
                type="button"
                class="cd-tabs__item"
                :class="{ 'is-active': tab === item.key }"
                @click="tab = item.key"
              >
                {{ item.label }}
              </button>
            </div>

            <section v-show="tab === 'intro'" class="cd-panel">
              <h2 class="cd-panel__title">课程简介</h2>
              <p class="cd-panel__lead">{{ course.summary }}</p>
              <p>
                本课程由天津高仁科技提供服务，采用「理论讲解 + 分组实操 + 案例复盘」模式，适合企业产线人员、自动化工程师及院校师生学习。支持企业团报，提交报名后我们将尽快联系您确认开班安排。
              </p>

              <h3 class="cd-panel__subtitle">你将学到</h3>
              <ul class="cd-learn">
                <li v-for="(item, idx) in learnPoints" :key="idx">{{ item }}</li>
              </ul>

              <h3 class="cd-panel__subtitle">适合人群</h3>
              <ul class="cd-audience">
                <li v-for="(item, idx) in audienceList" :key="idx">{{ item }}</li>
              </ul>

              <h3 class="cd-panel__subtitle">课程包含</h3>
              <div class="cd-includes">
                <span v-for="inc in (course.includes || ['文档', '图文'])" :key="inc">{{ inc }}</span>
              </div>
            </section>

            <section v-show="tab === 'chapters'" class="cd-panel cd-panel--chapters">
              <div class="cd-panel__head">
                <h2 class="cd-panel__title">课程目录</h2>
                <span>共 {{ course.chapters.length }} 节 · {{ course.duration }}</span>
              </div>

              <div class="cd-chapter-list">
                <article
                  v-for="(ch, idx) in course.chapters"
                  :key="idx"
                  class="cd-chapter"
                  :class="{ 'is-open': openChapter === idx }"
                >
                  <button type="button" class="cd-chapter__head" @click="toggleChapter(idx)">
                    <span class="cd-chapter__idx">{{ String(idx + 1).padStart(2, '0') }}</span>
                    <div class="cd-chapter__info">
                      <strong>{{ ch.title }}</strong>
                      <em>{{ ch.stage }} · {{ ch.duration }}</em>
                    </div>
                    <span class="cd-chapter__meta">
                      <i class="cd-chapter__lock">可报名学习</i>
                      <svg class="cd-chapter__arrow" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M6 9l6 6 6-6" />
                      </svg>
                    </span>
                  </button>
                  <div v-if="openChapter === idx" class="cd-chapter__body">
                    <p>本章节围绕「{{ ch.title }}」展开，包含{{ ch.stage }}讲解与实操指导。报名后可按培训安排参与线下实训与考核。</p>
                    <button type="button" class="cd-chapter__cta" @click="enrollOpen = true">报名学习本章</button>
                  </div>
                </article>
              </div>
            </section>

            <section v-show="tab === 'teacher'" class="cd-panel">
              <h2 class="cd-panel__title">授课团队</h2>
              <div class="cd-teacher">
                <div class="cd-teacher__avatar">高</div>
                <div>
                  <h3>自动化系统集成资深工程师</h3>
                  <p>天津高仁科技技术团队 · 多年机器人 / 视觉 / PLC / AGV 现场交付与培训经验</p>
                  <ul>
                    <li>企业产线联调与故障排查实战经验</li>
                    <li>院校实训课程建设与技能大赛辅导</li>
                    <li>支持按客户设备型号定制授课内容</li>
                  </ul>
                </div>
              </div>
            </section>

            <section v-show="tab === 'faq'" class="cd-panel">
              <h2 class="cd-panel__title">常见问题</h2>
              <div class="cd-faq">
                <details v-for="(item, idx) in faqList" :key="idx" class="cd-faq__item" :open="idx === 0">
                  <summary>{{ item.q }}</summary>
                  <p>{{ item.a }}</p>
                </details>
              </div>
            </section>

            <section v-show="tab === 'schedule'" class="cd-panel">
              <div class="cd-panel__head">
                <h2 class="cd-panel__title">培训安排</h2>
                <span v-if="linkedSchedule?.meta?.panelHint">{{ linkedSchedule.meta.panelHint }}</span>
              </div>

              <template v-if="scheduleDays.length">
                <p v-if="linkedSchedule?.meta?.scheduleText" class="cd-schedule__meta">
                  培训时间：{{ linkedSchedule.meta.scheduleText }}
                  <template v-if="linkedSchedule.meta.location">
                    · 地点：{{ linkedSchedule.meta.location }}
                  </template>
                </p>
                <ol class="cd-schedule">
                  <li v-for="day in scheduleDays" :key="day.day" class="cd-schedule__day">
                    <span class="cd-schedule__num">{{ day.day }}</span>
                    <div class="cd-schedule__body">
                      <header class="cd-schedule__head">
                        <span class="cd-schedule__badge">{{ day.dateLabel }} · {{ day.weekday }}</span>
                        <span class="cd-schedule__badge cd-schedule__badge--soft">第 {{ day.day }} 天 · {{ day.stage }}</span>
                        <span v-if="day.practiceRatio" class="cd-schedule__badge cd-schedule__badge--cat">
                          实操 {{ day.practiceRatio }}
                        </span>
                        <h3>{{ day.title }}</h3>
                      </header>
                      <p class="cd-schedule__desc">{{ day.content }}</p>
                      <ul v-if="day.topics?.length" class="cd-schedule__topics">
                        <li v-for="(topic, idx) in day.topics" :key="idx">{{ topic }}</li>
                      </ul>
                    </div>
                  </li>
                </ol>

                <div v-if="scheduleStages.length" class="cd-schedule-block">
                  <div class="cd-panel__head">
                    <h2 class="cd-panel__title">培训阶段说明</h2>
                    <span v-if="linkedSchedule?.meta?.stagesHint">{{ linkedSchedule.meta.stagesHint }}</span>
                  </div>
                  <div class="cd-stages">
                    <article v-for="(stage, idx) in scheduleStages" :key="idx" class="cd-stage">
                      <h3>{{ stage.title }}</h3>
                      <p>{{ stage.desc }}</p>
                    </article>
                  </div>
                </div>

                <div
                  v-if="schedulePurposes.length || scheduleNotices.length"
                  class="cd-schedule-aside"
                >
                  <section v-if="schedulePurposes.length" class="cd-schedule-aside__col">
                    <h3>培训目标</h3>
                    <ol>
                      <li v-for="(item, idx) in schedulePurposes" :key="idx">{{ item }}</li>
                    </ol>
                  </section>
                  <section v-if="scheduleNotices.length" class="cd-schedule-aside__col">
                    <h3>培训注意事项与配套要求</h3>
                    <ol>
                      <li v-for="(item, idx) in scheduleNotices" :key="idx">{{ item }}</li>
                    </ol>
                  </section>
                </div>
              </template>
              <p v-else class="cd-schedule__empty">该课程暂未关联十天培训安排表，请联系教务老师确认开班时间。</p>
            </section>
          </div>

          <!-- 右侧报名卡（桌面吸顶） -->
          <aside class="cd-side">
            <div class="cd-card">
              <div class="cd-card__cover" :class="`cd-card__cover--${course.coverTone}`">
                <strong>{{ course.shortTitle || course.category }}</strong>
              </div>
              <div class="cd-card__body">
                <div class="cd-card__price">
                  <em>课程报价</em>
                  <strong>{{ formatCoursePrice(course.price) }}</strong>
                  <span class="cd-card__unit">/ 人</span>
                </div>
                <button type="button" class="cd-card__btn" @click="enrollOpen = true">立即报名</button>
                <button
                  v-if="course.scheduleRoute"
                  type="button"
                  class="cd-card__btn cd-card__btn--ghost"
                  @click="goSchedule"
                >
                  查看培训安排
                </button>
                <a
                  v-if="linkedSchedule?.meta?.scheduleFile"
                  class="cd-card__btn cd-card__btn--ghost"
                  :href="linkedSchedule.meta.scheduleFile"
                  :download="linkedSchedule.meta.scheduleFileName"
                >
                  下载安排表
                </a>
                <ul class="cd-card__meta">
                  <li><span>课程报价</span><strong>{{ formatCoursePrice(course.price) }} / 人</strong></li>
                  <li><span>课程分类</span><strong>{{ course.category }}</strong></li>
                  <li><span>学习时长</span><strong>{{ course.duration }}</strong></li>
                  <li><span>章节数量</span><strong>{{ course.chapters.length }} 节</strong></li>
                  <li><span>学习人数</span><strong>{{ course.learners }} 人</strong></li>
                  <li><span>培训形式</span><strong>理论 + 实操</strong></li>
                  <li><span>开班安排</span><strong>8–11月十天</strong></li>
                </ul>
                <p class="cd-card__tip">报名后教务老师将电话确认开班时间与培训地点。</p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <!-- 移动端底部栏 -->
      <div class="cd-mobile-bar">
        <div class="cd-mobile-bar__inner">
          <div class="cd-mobile-bar__text">
            <span>{{ course.shortTitle || course.category }}</span>
            <strong>{{ course.title }}</strong>
          </div>
          <button type="button" class="cd-card__btn" @click="enrollOpen = true">立即报名</button>
        </div>
      </div>
    </template>

    <AcademyEnrollModal v-model="enrollOpen" :course="course" />
  </AcademyLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import { getAcademyCourse, getTrainingSchedule, formatCoursePrice } from '../data/trainingCatalog'
import { company } from '../data/siteConfig'

const route = useRoute()
const tab = ref('intro')
const enrollOpen = ref(false)
const openChapter = ref(0)
const panelRef = ref(null)

const tabs = [
  { key: 'intro', label: '课程介绍' },
  { key: 'chapters', label: '课程目录' },
  { key: 'teacher', label: '授课团队' },
  { key: 'faq', label: '常见问题' },
  { key: 'schedule', label: '培训安排' },
]

const course = computed(() => getAcademyCourse(route.params.courseId))

const linkedSchedule = computed(() => {
  if (!course.value?.scheduleRoute) return null
  return getTrainingSchedule(course.value.scheduleId || 'vision')
})

const scheduleDays = computed(() => linkedSchedule.value?.courses || [])
const scheduleStages = computed(() => linkedSchedule.value?.stages || [])
const schedulePurposes = computed(() => linkedSchedule.value?.purposes || [])
const scheduleNotices = computed(() => linkedSchedule.value?.notices || [])

const learnPoints = computed(() => {
  const c = course.value
  if (!c) return []
  const fromChapters = c.chapters.map((ch) => `掌握「${ch.title}」相关要点与实操方法`)
  if (fromChapters.length >= 3) return fromChapters.slice(0, 5)
  return [
    `系统学习${c.shortTitle || c.category}核心知识与现场规范`,
    '完成典型任务实操，具备基础联调与排故能力',
    '建立参数备份、版本管理与交付维护思路',
  ]
})

const audienceList = computed(() => {
  const c = course.value
  if (!c) return []
  return [
    `${c.category}相关岗位的操作、运维与调试人员`,
    '企业自动化工程师、产线班组长与设备维护人员',
    '职业院校师生及技能大赛辅导教师',
  ]
})

const faqList = computed(() => {
  const scheduleText = linkedSchedule.value?.meta?.scheduleText || '近期开班安排'
  return [
    {
      q: '如何报名？报名后多久开班？',
      a: `点击「立即报名」填写信息即可。教务老师会在 1–2 个工作日内电话确认。本期培训安排：${scheduleText}，共十天完成培训考试。`,
    },
    {
      q: '课程是线上还是线下？',
      a: '以线下理论讲解与分组实操为主。部分资料可在知识资料区查阅。',
    },
    {
      q: '没有基础可以学吗？',
      a: '可以。课程从基础安全与原理讲起，再进入实操联调。建议提前准备笔记本电脑（Win10、内存16G及以上）。',
    },
    {
      q: '支持企业团报吗？',
      a: '支持。企业可按岗位批量报名，也可按现场设备型号定制授课重点，请在报名备注中说明需求。',
    },
  ]
})

watch(
  course,
  (c) => {
    tab.value = 'intro'
    openChapter.value = 0
    window.scrollTo({ top: 0 })
    if (c?.title) {
      document.title = `${c.shortTitle || c.title} - ${company.name || '天津高仁科技'}`
    }
  },
  { immediate: true }
)

function toggleChapter(idx) {
  openChapter.value = openChapter.value === idx ? -1 : idx
}

function scrollToPanel() {
  panelRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function goSchedule() {
  tab.value = 'schedule'
  scrollToPanel()
}
</script>

<style scoped>
.ac-wrap {
  width: min(1160px, calc(100% - 40px));
  margin: 0 auto;
}

.ac-empty {
  padding: 80px 0;
  text-align: center;
  color: #666;
}

.ac-empty a {
  color: #2d8cf0;
}

/* ===== Hero ===== */
.cd-hero {
  position: relative;
  color: #fff;
  background: linear-gradient(135deg, #1a5f9e 0%, #2d8cf0 55%, #5babff 100%);
  overflow: hidden;
}

.cd-hero--2 { background: linear-gradient(135deg, #0f7a45 0%, #19be6b 55%, #47d08a 100%); }
.cd-hero--3 { background: linear-gradient(135deg, #c45300 0%, #ff6a00 55%, #ff9345 100%); }
.cd-hero--4 { background: linear-gradient(135deg, #12253f 0%, #1f4e79 55%, #2b85e4 100%); }

.cd-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 85% 20%, rgba(255, 255, 255, 0.14), transparent 40%),
    radial-gradient(circle at 10% 80%, rgba(0, 0, 0, 0.12), transparent 45%);
  pointer-events: none;
}

.cd-hero__inner {
  position: relative;
  z-index: 1;
  padding: 22px 0 36px;
}

.cd-crumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}

.cd-crumb a {
  color: rgba(255, 255, 255, 0.86);
  text-decoration: none;
}

.cd-crumb a:hover {
  color: #fff;
  text-decoration: underline;
}

.cd-crumb em {
  font-style: normal;
  color: #fff;
}

.cd-hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 36px;
  align-items: center;
}

.cd-hero__badge {
  display: inline-block;
  margin-bottom: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.18);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.cd-hero__main h1 {
  margin: 0 0 14px;
  font-size: clamp(26px, 3.4vw, 36px);
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.01em;
}

.cd-hero__desc {
  margin: 0 0 18px;
  max-width: 640px;
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.9);
}

.cd-hero__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 22px;
  margin-bottom: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
}

.cd-hero__stats strong {
  margin-right: 4px;
  font-size: 16px;
  color: #fff;
}

.cd-hero__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cd-hero__tags span {
  padding: 3px 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  font-size: 12px;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.cd-hero__media {
  min-width: 0;
}

.cd-hero__poster {
  position: relative;
  aspect-ratio: 16 / 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.cd-hero__poster::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(160deg, rgba(255, 255, 255, 0.12), transparent 50%),
    repeating-linear-gradient(
      -25deg,
      transparent,
      transparent 18px,
      rgba(255, 255, 255, 0.03) 18px,
      rgba(255, 255, 255, 0.03) 19px
    );
}

.cd-hero__poster strong,
.cd-hero__poster em,
.cd-hero__play {
  position: relative;
  z-index: 1;
}

.cd-hero__poster strong {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.cd-hero__poster em {
  font-style: normal;
  font-size: 13px;
  opacity: 0.85;
}

.cd-hero__play {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #1f2937;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.cd-hero__play:hover {
  background: #fff;
}

/* ===== Body layout ===== */
.cd-body {
  padding: 20px 0 100px;
}

.cd-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

.cd-main {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e8eef5;
  box-shadow: 0 6px 20px rgba(30, 55, 90, 0.04);
  overflow: hidden;
}

.cd-tabs {
  display: flex;
  gap: 4px;
  padding: 0 8px;
  border-bottom: 1px solid #eef2f7;
  background: #fafbfd;
  position: sticky;
  top: 0;
  z-index: 5;
}

.cd-tabs__item {
  position: relative;
  padding: 16px 18px;
  border: none;
  background: transparent;
  font-size: 15px;
  color: #667085;
  cursor: pointer;
}

.cd-tabs__item.is-active {
  color: #2d8cf0;
  font-weight: 700;
}

.cd-tabs__item.is-active::after {
  content: '';
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 0;
  height: 2px;
  background: #2d8cf0;
  border-radius: 2px 2px 0 0;
}

.cd-panel {
  padding: 24px 28px 32px;
}

.cd-panel__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.cd-panel__head span {
  font-size: 13px;
  color: #98a2b3;
}

.cd-panel__title {
  margin: 0 0 14px;
  font-size: 20px;
  color: #152033;
}

.cd-panel__subtitle {
  margin: 24px 0 12px;
  font-size: 16px;
  color: #1f2937;
}

.cd-panel__lead {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.8;
  color: #334155;
}

.cd-panel p {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.8;
  color: #5b6573;
}

.cd-learn,
.cd-audience {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.cd-learn li,
.cd-audience li {
  position: relative;
  padding: 12px 14px 12px 36px;
  background: #f7f9fc;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
}

.cd-learn li::before,
.cd-audience li::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 16px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2d8cf0;
  box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.15);
}

.cd-includes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cd-includes span {
  padding: 6px 12px;
  border-radius: 6px;
  background: #eef6ff;
  color: #2d8cf0;
  font-size: 13px;
  font-weight: 600;
}

/* chapters */
.cd-chapter-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cd-chapter {
  border: 1px solid #e8eef5;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.cd-chapter.is-open {
  border-color: rgba(45, 140, 240, 0.35);
  box-shadow: 0 8px 20px rgba(45, 140, 240, 0.08);
}

.cd-chapter__head {
  width: 100%;
  display: grid;
  grid-template-columns: 48px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border: none;
  background: #fafbfd;
  text-align: left;
  cursor: pointer;
}

.cd-chapter.is-open .cd-chapter__head {
  background: #f3f8ff;
}

.cd-chapter__idx {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e8eef5;
  color: #2d8cf0;
  font-size: 13px;
  font-weight: 800;
}

.cd-chapter__info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cd-chapter__info strong {
  font-size: 15px;
  color: #152033;
}

.cd-chapter__info em {
  font-style: normal;
  font-size: 12px;
  color: #98a2b3;
}

.cd-chapter__meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #98a2b3;
}

.cd-chapter__lock {
  font-style: normal;
  font-size: 12px;
  color: #2d8cf0;
  font-weight: 600;
}

.cd-chapter.is-open .cd-chapter__arrow {
  transform: rotate(180deg);
}

.cd-chapter__arrow {
  transition: transform 0.2s ease;
}

.cd-chapter__body {
  padding: 0 16px 16px;
  border-top: 1px solid #eef2f7;
  background: #fff;
}

.cd-chapter__body p {
  margin: 12px 0;
  font-size: 13px;
  line-height: 1.7;
  color: #667085;
}

.cd-chapter__cta {
  padding: 8px 16px;
  border: 1px solid #ff6a00;
  border-radius: 6px;
  background: #fff;
  color: #ff6a00;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.cd-chapter__cta:hover {
  background: #fff7f0;
}

/* teacher */
.cd-teacher {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 16px;
  padding: 18px;
  background: #f7f9fc;
  border-radius: 12px;
}

.cd-teacher__avatar {
  width: 72px;
  height: 72px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #2d8cf0, #5babff);
  color: #fff;
  font-size: 28px;
  font-weight: 800;
}

.cd-teacher h3 {
  margin: 0 0 6px;
  font-size: 16px;
  color: #152033;
}

.cd-teacher p {
  margin: 0 0 10px;
}

.cd-teacher ul {
  margin: 0;
  padding-left: 18px;
  color: #5b6573;
  font-size: 13px;
  line-height: 1.8;
}

/* faq */
.cd-faq {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cd-faq__item {
  border: 1px solid #e8eef5;
  border-radius: 10px;
  padding: 0 16px;
  background: #fff;
}

.cd-faq__item summary {
  padding: 14px 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  list-style: none;
}

.cd-faq__item summary::-webkit-details-marker {
  display: none;
}

.cd-faq__item p {
  margin: 0 0 14px;
  padding-top: 0;
  border-top: 1px solid #eef2f7;
  padding-top: 12px;
}

/* schedule */
.cd-schedule__meta {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #667085;
}

.cd-schedule__empty {
  margin: 0;
  font-size: 14px;
  color: #98a2b3;
}

.cd-schedule {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cd-schedule__day {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  align-items: start;
}

.cd-schedule__num {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: 2px solid #2d8cf0;
  background: #fff;
  color: #2d8cf0;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.1);
}

.cd-schedule__body {
  padding: 14px 16px 12px;
  border: 1px solid #e8eef5;
  border-radius: 10px;
  background: #fff;
}

.cd-schedule__day:nth-child(even) .cd-schedule__body {
  background: #eef5fb;
  border-color: #dce8f5;
}

.cd-schedule__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.cd-schedule__head h3 {
  margin: 0;
  flex: 1 1 100%;
  font-size: 15px;
  font-weight: 700;
  color: #152033;
  line-height: 1.4;
}

.cd-schedule__badge {
  display: inline-flex;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(45, 140, 240, 0.12);
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 700;
}

.cd-schedule__badge--soft {
  background: #eef2f7;
  color: #667085;
}

.cd-schedule__badge--cat {
  background: rgba(255, 106, 0, 0.1);
  color: #ff6a00;
}

.cd-schedule__desc {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.7;
  color: #5b6573;
}

.cd-schedule__topics {
  margin: 0;
  padding-left: 18px;
  color: #5b6573;
}

.cd-schedule__topics li {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 1.65;
}

.cd-schedule-block {
  margin-top: 28px;
  padding-top: 8px;
}

.cd-stages {
  display: grid;
  gap: 12px;
}

.cd-stage {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f7f9fc;
  border: 1px solid #eef2f7;
}

.cd-stage h3 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #152033;
}

.cd-stage p {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #5b6573;
}

.cd-schedule-aside {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 20px;
}

.cd-schedule-aside__col {
  background: #f7f9fc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 18px 18px 10px;
}

.cd-schedule-aside__col h3 {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8eef5;
  font-size: 15px;
  color: #152033;
}

.cd-schedule-aside__col ol {
  margin: 0;
  padding-left: 18px;
}

.cd-schedule-aside__col li {
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.7;
  color: #5b6573;
}

@media (max-width: 760px) {
  .cd-schedule-aside {
    grid-template-columns: 1fr;
  }
}

/* ===== Side card ===== */
.cd-side {
  position: sticky;
  top: 16px;
}

.cd-card {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(30, 55, 90, 0.08);
}

.cd-card__cover {
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(145deg, #2d8cf0, #5babff);
}

.cd-card__cover--2 { background: linear-gradient(145deg, #19be6b, #47d08a); }
.cd-card__cover--3 { background: linear-gradient(145deg, #ff6a00, #ff9345); }
.cd-card__cover--4 { background: linear-gradient(145deg, #2b85e4, #17233d); }

.cd-card__cover strong {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.cd-card__body {
  padding: 18px 18px 20px;
}

.cd-card__price {
  margin-bottom: 14px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}

.cd-card__price em {
  display: block;
  width: 100%;
  margin-bottom: 4px;
  font-style: normal;
  font-size: 12px;
  color: #98a2b3;
}

.cd-card__price strong {
  font-size: 28px;
  color: #ff6a00;
  font-weight: 800;
}

.cd-card__unit {
  font-size: 13px;
  color: #98a2b3;
}

.cd-card__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background: #ff6a00;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(255, 106, 0, 0.25);
}

.cd-card__btn + .cd-card__btn {
  margin-top: 10px;
}

.cd-card__btn:hover {
  background: #e85f00;
}

.cd-card__btn--ghost {
  background: #fff;
  color: #ff6a00;
  border: 1px solid #ff6a00;
  box-shadow: none;
}

.cd-card__btn--ghost:hover {
  background: #fff7f0;
}

.cd-card__meta {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
}

.cd-card__meta li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px dashed #eef2f7;
  font-size: 13px;
}

.cd-card__meta li span {
  color: #98a2b3;
}

.cd-card__meta li strong {
  color: #334155;
  font-weight: 600;
  text-align: right;
}

.cd-card__tip {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #98a2b3;
}

/* mobile bar */
.cd-mobile-bar {
  display: none;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.06);
}

.cd-mobile-bar__inner {
  width: min(1160px, calc(100% - 24px));
  margin: 0 auto;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cd-mobile-bar__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cd-mobile-bar__text span {
  font-size: 12px;
  font-weight: 700;
  color: #2d8cf0;
}

.cd-mobile-bar__text strong {
  font-size: 13px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cd-mobile-bar .cd-card__btn {
  width: auto;
  flex-shrink: 0;
  padding: 10px 20px;
  box-shadow: none;
}

@media (max-width: 980px) {
  .cd-hero__grid,
  .cd-layout {
    grid-template-columns: 1fr;
  }

  .cd-side {
    position: static;
    order: -1;
  }

  .cd-hero__media {
    display: none;
  }
}

@media (max-width: 760px) {
  .ac-wrap {
    width: calc(100% - 24px);
  }

  .cd-panel {
    padding: 18px 16px 24px;
  }

  .cd-tabs {
    overflow-x: auto;
  }

  .cd-tabs__item {
    flex-shrink: 0;
    padding: 14px 12px;
    font-size: 14px;
  }

  .cd-chapter__head {
    grid-template-columns: 40px 1fr;
  }

  .cd-chapter__meta {
    display: none;
  }

  .cd-teacher {
    grid-template-columns: 1fr;
  }

  .cd-mobile-bar {
    display: block;
  }

  .cd-body {
    padding-bottom: 88px;
  }
}
</style>
